import React, { useEffect, useState } from "react";
import MobileLayout from "../components/layout/MobileLayout";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Crown, Clock, Trophy, XCircle, ListChecks, Dices, Rocket, Flame, Coins } from "lucide-react";

const statusMeta = {
  pending: { color: "from-amber-400 to-yellow-500", text: "text-amber-950", label: "PENDING", Icon: Clock },
  won:     { color: "from-emerald-400 to-emerald-600", text: "text-emerald-950", label: "WON",   Icon: Trophy },
  lost:    { color: "from-rose-500 to-rose-700", text: "text-white", label: "LOST",  Icon: XCircle },
};

const CASINO_META = {
  aviator:      { label: "AVIATOR",      icon: Rocket,  hue: "from-fuchsia-600 to-rose-600" },
  dragon_tiger: { label: "DRAGON TIGER", icon: Flame,   hue: "from-rose-700 to-amber-500" },
  coin_flip:    { label: "COIN FLIP",    icon: Coins,   hue: "from-amber-600 to-yellow-500" },
};

export default function MyBids() {
  const [bids, setBids] = useState([]);
  const [casino, setCasino] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingC, setLoadingC] = useState(true);

  useEffect(() => {
    api.get("/bids/me").then(({ data }) => setBids(data || [])).finally(() => setLoading(false));
    api.get("/user/casino-history?limit=100").then(({ data }) => setCasino(Array.isArray(data) ? data : [])).finally(() => setLoadingC(false));
  }, []);

  const matkaTabs = [
    { key: "all",     label: "All",     filter: () => true },
    { key: "pending", label: "Pending", filter: (b) => b.status === "pending" },
    { key: "won",     label: "Won",     filter: (b) => b.status === "won" },
    { key: "lost",    label: "Lost",    filter: (b) => b.status === "lost" },
  ];

  return (
    <MobileLayout>
      <div className="relative rounded-2xl bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 border-2 border-yellow-400/50 shadow-xl p-4 mb-4 overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-25" />
        <div className="absolute -top-6 -right-6 w-20 h-20 bg-yellow-400/20 rounded-full blur-2xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gold-gradient text-blue-950 flex items-center justify-center shadow-lg">
            <ListChecks className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Crown className="w-4 h-4 text-yellow-400" fill="currentColor" />
              <span className="font-display font-black text-xl text-gold-static leading-none">My Bets</span>
            </div>
            <div className="text-[10px] font-black tracking-[0.2em] text-blue-200 mt-1">MATKA + CASINO HISTORY</div>
          </div>
        </div>
      </div>

      {/* Top-level: MATKA vs CASINO */}
      <Tabs defaultValue="matka" data-testid="mybids-main-tabs">
        <TabsList className="grid grid-cols-2 bg-blue-950/90 border border-yellow-400/40 rounded-xl mb-3 p-1 h-11">
          <TabsTrigger value="matka" data-testid="tab-matka"
            className="rounded-lg font-black text-xs tracking-wider data-[state=active]:bg-gold-gradient data-[state=active]:text-blue-950 text-blue-200">
            🎰 MATKA ({bids.length})
          </TabsTrigger>
          <TabsTrigger value="casino" data-testid="tab-casino"
            className="rounded-lg font-black text-xs tracking-wider data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-rose-500 data-[state=active]:text-white text-blue-200">
            🎲 CASINO ({casino.length})
          </TabsTrigger>
        </TabsList>

        {/* MATKA tab */}
        <TabsContent value="matka">
          <Tabs defaultValue="all" data-testid="matka-status-tabs">
            <TabsList className="grid grid-cols-4 bg-blue-950/90 border border-yellow-400/40 rounded-xl mb-3 p-1 h-10">
              {matkaTabs.map(t => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}
                  className="rounded-lg font-black text-xs tracking-wider data-[state=active]:bg-gold-gradient data-[state=active]:text-blue-950 text-blue-200">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {matkaTabs.map(t => (
              <TabsContent key={t.key} value={t.key}>
                {loading ? <Skeleton /> : (
                  <div className="space-y-2" data-testid={`bids-list-${t.key}`}>
                    {bids.filter(t.filter).length === 0 ? <Empty label="Matka bid" /> :
                      bids.filter(t.filter).map(b => <BidCard key={b.id} b={b} />)}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        {/* CASINO tab */}
        <TabsContent value="casino">
          {loadingC ? <Skeleton /> : (
            <div className="space-y-2" data-testid="casino-list">
              {casino.length === 0 ? <Empty label="Casino bet" /> :
                casino.map(c => <CasinoBetCard key={c.id} b={c} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </MobileLayout>
  );
}

function BidCard({ b }) {
  const meta = statusMeta[b.status] || statusMeta.pending;
  const { Icon } = meta;
  return (
    <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 border border-yellow-400/40 rounded-2xl p-3 shadow-xl overflow-hidden" data-testid={`bid-${b.id}`}>
      <div className="absolute inset-0 pattern-grid opacity-15" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-display font-black text-yellow-300 text-base leading-none truncate">{b.market_name}</div>
            <div className="text-[11px] text-blue-200 mt-1 font-semibold">{b.game_name} · {b.session || "—"}</div>
          </div>
          <div className={`shrink-0 inline-flex items-center gap-1 bg-gradient-to-r ${meta.color} ${meta.text} text-[10px] font-black tracking-widest px-2.5 py-1 rounded-full shadow`}>
            <Icon className="w-3 h-3" strokeWidth={2.5} />
            {meta.label}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="font-mono tracking-widest font-black text-2xl text-yellow-400 tabular-nums drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]">{b.number}</div>
          <div className="text-right text-xs text-blue-200">
            Bid: <span className="tabular-nums text-white font-bold">₹{b.amount}</span>
            <span className="mx-1 opacity-40">·</span>
            Win: <span className={`tabular-nums font-black ${b.status === "won" ? "text-emerald-300" : "text-yellow-200/70"}`}>₹{b.win_amount || 0}</span>
          </div>
        </div>
        <div className="text-[10px] text-blue-300/70 mt-1.5 tabular-nums">{new Date(b.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}

function CasinoBetCard({ b }) {
  const g = CASINO_META[b.game_type] || { label: (b.game_type || "GAME").toUpperCase(), icon: Dices, hue: "from-slate-700 to-slate-900" };
  const s = (b.status || "").toLowerCase();
  const statusBadge = s === "won"
    ? { text: `+₹${b.payout ?? 0}`, cls: "text-emerald-200 bg-emerald-500/20 border-emerald-400/40" }
    : s === "lost"
      ? { text: `-₹${b.amount ?? 0}`, cls: "text-rose-200 bg-rose-500/20 border-rose-400/40" }
      : { text: "Pending", cls: "text-amber-200 bg-amber-500/20 border-amber-400/40" };
  const Icon = g.icon;
  return (
    <div className={`relative flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r ${g.hue} text-white shadow-lg overflow-hidden`} data-testid={`casino-bid-${b.id}`}>
      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
      <div className="relative flex-shrink-0 w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
        <Icon className="w-5 h-5" />
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black tracking-wider uppercase bg-white/20 px-1.5 py-0.5 rounded">{g.label}</span>
        </div>
        <div className="text-sm font-medium mt-0.5 truncate">
          Pick: <span className="font-black">{b.pick || b.choice || b.side || "—"}</span>
          {b.cashout_multiplier ? <span className="text-amber-300 ml-1">@ {Number(b.cashout_multiplier).toFixed(2)}x</span> : null}
        </div>
        <div className="text-[10px] text-white/70 mt-0.5">{new Date(b.created_at).toLocaleString("en-IN", { hour12: true })}</div>
      </div>
      <div className="relative text-right flex flex-col items-end gap-1">
        <div className="text-[10px] text-white/80">Stake ₹{b.amount || 0}</div>
        <div className={`text-xs font-black px-2 py-1 rounded-md border ${statusBadge.cls}`}>{statusBadge.text}</div>
      </div>
    </div>
  );
}

const Skeleton = () => <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-blue-900/40 rounded-2xl animate-pulse" />)}</div>;
const Empty = ({ label = "bet" }) => (
  <div className="text-center py-12 text-blue-300 text-sm border-2 border-dashed border-yellow-400/30 rounded-2xl bg-blue-950/40">
    <Crown className="w-10 h-10 text-yellow-400/50 mx-auto mb-2" />
    Abhi tak koi {label} nahi lagayi
  </div>
);
